import React, { useEffect, useState } from 'react';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import TextField from 'material-ui/TextField';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import LinearProgress from 'material-ui/LinearProgress';
import { getLetter } from 'services/letters';
import { addCorpusMail } from 'services/testRuns';

const OBJECT_ID = /^[a-fA-F0-9]{24}$/;
const UUID = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/;

export function parseMailIds(input) {
    const tokens = String(input || '').split(/[\s,;]+/).filter(Boolean);
    const ids = [];
    const seen = {};
    tokens.forEach((token) => {
        const id = extractMailId(token);
        if (id && !seen[id]) {
            seen[id] = true;
            ids.push(id);
        }
    });
    return ids;
}

function extractMailId(raw) {
    const token = String(raw).trim();
    if (!token) {
        return null;
    }
    let path = token;
    try {
        const withProto = /^[a-z]+:\/\//i.test(token)
            ? token
            : (/^[a-z0-9.-]+(:\d+)?\//i.test(token) ? `https://${token}` : null);
        if (withProto) {
            path = new URL(withProto).pathname;
        }
    } catch (e) {
        path = token;
    }
    const parts = path.split('/').filter(Boolean);
    for (let i = 0; i < parts.length; i += 1) {
        if (OBJECT_ID.test(parts[i]) || UUID.test(parts[i])) {
            return parts[i];
        }
    }
    if (OBJECT_ID.test(token) || UUID.test(token)) {
        return token;
    }
    if (token.indexOf('/') === -1 && token.indexOf('.') === -1) {
        return token;
    }
    return parts[0] || null;
}

async function loadLetter(id) {
    const response = await getLetter(id);
    if (!response || response.status !== 200 || !response.data) {
        const firstError = response && response.errors && response.errors[0];
        const message = (firstError && (Array.isArray(firstError.messages)
            ? firstError.messages[0]
            : firstError.message))
            || (response && (response.message || (response.messages && response.messages[0])))
            || `letter ${id} not found`;
        throw new Error(message);
    }
    return response.data;
}

function toMailPayload(letter, fallbackId) {
    const sourceMailId = String(letter._id || fallbackId);
    const text = String(letter.text || '').trim();
    if (!text) {
        throw new Error(`letter ${sourceMailId} has no text`);
    }
    const subject = String(letter.subject || '').trim();
    if (!subject) {
        throw new Error(`letter ${sourceMailId} has no subject`);
    }
    const dateVal = letter.date || letter.originalDate || letter.receivedAt;
    const received = dateVal ? new Date(dateVal) : null;
    if (!received || Number.isNaN(received.getTime())) {
        throw new Error(`letter ${sourceMailId} has no received date`);
    }
    return {
        sourceMailId,
        text,
        subject,
        receivedAt: received.toISOString(),
    };
}

export default function AddMailsDialog({ open, corpusId, existingSourceIds, onClose, onAdded }) {
    const [input, setInput] = useState('');
    const [annotationType, setAnnotationType] = useState('cargo');
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setInput('');
            setAnnotationType('cargo');
            setResults([]);
            setProgress(null);
            setError('');
            setBusy(false);
        }
    }, [open]);

    const close = () => {
        if (busy) {
            return;
        }
        onClose();
    };

    const submit = async () => {
        const ids = parseMailIds(input);
        if (!ids.length) {
            setError('Paste mail ids or admin.shipnext.com/<id> links');
            return;
        }
        const seen = (existingSourceIds || []).slice();
        setBusy(true);
        setError('');
        setResults([]);
        const nextResults = [];
        let added = 0;
        for (let i = 0; i < ids.length; i += 1) {
            const id = ids[i];
            setProgress({ done: i, total: ids.length, current: id });
            if (seen.indexOf(id) >= 0) {
                nextResults.push({ id, status: 'skipped', message: 'already in corpus' });
                setResults(nextResults.slice());
                continue;
            }
            try {
                const letter = await loadLetter(id);
                const payload = toMailPayload(letter, id);
                if (seen.indexOf(payload.sourceMailId) >= 0) {
                    nextResults.push({ id, status: 'skipped', message: 'already in corpus' });
                    setResults(nextResults.slice());
                    continue;
                }
                await addCorpusMail(corpusId, { ...payload, annotationType });
                seen.push(payload.sourceMailId);
                added += 1;
                nextResults.push({ id, status: 'added' });
            } catch (err) {
                const message = err.message || String(err);
                const skipped = /already in corpus/.test(message);
                nextResults.push({
                    id,
                    status: skipped ? 'skipped' : 'failed',
                    message,
                });
            }
            setResults(nextResults.slice());
        }
        setProgress({ done: ids.length, total: ids.length });
        setBusy(false);
        if (added > 0 && onAdded) {
            onAdded();
        }
    };

    return (
        <Dialog
            title="Add documents"
            open={open}
            onRequestClose={close}
            autoScrollBodyContent
            actions={[
                <FlatButton key="close" label={busy ? 'Working…' : 'Close'} onClick={close} disabled={busy} />,
                <FlatButton key="add" label="Add" primary onClick={submit} disabled={busy} />,
            ]}
        >
            <p className="test-runs-hint">
                Paste mail ids or links such as admin.shipnext.com/&lt;id&gt;, one per line.
                Letters are loaded in this browser, then stored in the same order with the selected model.
            </p>
            <SelectField
                floatingLabelText="Model"
                value={annotationType}
                onChange={(e, i, value) => setAnnotationType(value)}
                fullWidth
                disabled={busy}
            >
                <MenuItem value="cargo" primaryText="cargo" />
                <MenuItem value="vessel" primaryText="vessel" />
            </SelectField>
            <TextField
                hintText={'507f1f77bcf86cd799439011\nhttps://admin.shipnext.com/507f1f77bcf86cd799439011'}
                value={input}
                onChange={(e, value) => setInput(value)}
                multiLine
                rows={6}
                rowsMax={12}
                fullWidth
                disabled={busy}
            />
            {error ? <div className="test-runs-error">{error}</div> : null}
            {progress ? (
                <div>
                    <LinearProgress
                        mode="determinate"
                        value={progress.total ? (progress.done / progress.total) * 100 : 0}
                    />
                    <p className="test-runs-hint">
                        {progress.done}/{progress.total}
                        {progress.current ? ` · ${progress.current}` : ''}
                    </p>
                </div>
            ) : null}
            {results.length ? (
                <ul className="bbr-add-results">
                    {results.map(row => (
                        <li key={row.id} className={`bbr-add-result-${row.status}`}>
                            {row.id} — {row.status}{row.message ? `: ${row.message}` : ''}
                        </li>
                    ))}
                </ul>
            ) : null}
        </Dialog>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/AddMailsDialog.jsx